import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// User APIs (RBAC)
export const userApi = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getSalespersons: () => api.get('/users/salespersons'),
  getManagers: () => api.get('/users/managers'),
  getMyTeam: () => api.get('/users/my-team'),
  updateLocation: (userId, data) => api.put(`/users/${userId}/location`, data),
};

// Customer APIs
export const customerApi = {
  create: (data) => api.post('/customers', data),
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  geocode: (address, city, state) => api.get('/geocode', { params: { address, city, state } }),
  fetchAddressFromWebsite: (url) => api.post('/fetch-address-from-website', { url }),
  importFile: (formData) => api.post('/customers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportData: (format, params) => api.get('/exports/customers', { params: { format, ...params }, responseType: 'blob' }),
};

// Activity APIs
export const activityApi = {
  create: (data) => api.post('/activities', data),
  getAll: (params) => api.get('/activities', { params }),
  getById: (id) => api.get(`/activities/${id}`),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
  checkIn: (id, data) => api.post(`/activities/${id}/checkin`, data),
};

// Analytics APIs
export const analyticsApi = {
  getRealtime: () => api.get('/analytics/realtime'),
  getPerformance: (period) => api.get('/analytics/performance', { params: { period } }),
  getCharts: () => api.get('/analytics/charts'),
};

// Search API
export const searchApi = {
  global: (q, module) => api.get('/search', { params: { q, module } }),
};

// Email API
export const emailApi = {
  getStatus: () => api.get('/email/status'),
  sendTest: (to) => api.post('/email/test', { to }),
};

// Onboarding API
export const onboardingApi = {
  get: () => api.get('/onboarding'),
  completeStep: (step_id) => api.post('/onboarding/complete-step', { step_id }),
  skip: () => api.post('/onboarding/skip'),
};

// Task APIs
export const taskApi = {
  create: (data) => api.post('/tasks', data),
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getNearby: (id, radius) => api.get(`/tasks/nearby/${id}`, { params: { radius_km: radius } }),
};

// Check-in APIs
export const checkinApi = {
  create: (data) => api.post('/checkins', data),
  getAll: (params) => api.get('/checkins', { params }),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/checkins/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Dashboard APIs
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getTeamLocations: () => api.get('/dashboard/team-locations'),
  getTeamTrails: (hours) => api.get('/dashboard/team-trails', { params: { hours } }),
};

// Notification APIs
export const notificationApi = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Payment APIs
export const paymentApi = {
  getPlans: () => api.get('/payments/plans'),
  createCheckout: (data) => api.post('/payments/checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  getMySubscription: () => api.get('/payments/my-subscription'),
};

// Export APIs
export const exportApi = {
  tasks: (format, params) => api.get(`/exports/tasks`, { params: { format, ...params }, responseType: 'blob' }),
  activities: (format, params) => api.get(`/exports/activities`, { params: { format, ...params }, responseType: 'blob' }),
  performance: (format, period) => api.get(`/exports/performance`, { params: { format, period }, responseType: 'blob' }),
};

// AI APIs
export const aiApi = {
  assistant: (data) => api.post('/ai/assistant', data),
  taskCommand: (command) => api.post('/ai/task-command', { command }),
  smartPrioritization: (data) => api.post('/ai/smart-task-prioritization', data),
  customerInsights: (data) => api.post('/ai/customer-insights', data),
  dailySummary: (data) => api.post('/ai/daily-summary', data),
  smartVisitPlan: (data) => api.post('/ai/smart-visit-plan', data),
  smartNotes: (data) => api.post('/ai/smart-notes', data),
  getFeatures: () => api.get('/ai/features'),
};

// Audit APIs
export const auditApi = {
  getLogs: (params) => api.get('/audit/logs', { params }),
  getStats: () => api.get('/audit/stats'),
  getActions: () => api.get('/audit/actions'),
};

// Subscription/Tier APIs
export const subscriptionApi = {
  getMyTier: () => api.get('/subscription/my-tier'),
  switchTier: (tier) => api.post('/subscription/switch-tier', { tier }),
  checkFeature: (feature) => api.post('/subscription/check-feature', { feature }),
  upgrade: (targetTier) => api.post('/subscription/upgrade', { target_tier: targetTier }),
};

export const beatsApi = {
  startBeat: (data) => api.post('/beats/start', data),
  ping: (data) => api.post('/beats/ping', data),
  endBeat: (data) => api.post('/beats/end', data),
  getMyActive: () => api.get('/beats/my-active'),
  getLive: () => api.get('/beats/live'),
  getLiveUsers: () => api.get('/beats/live-users'),
  getTrail: (beatId) => api.get(`/beats/live/${beatId}/trail`),
  getHistory: (params) => api.get('/beats/history', { params }),
  getLocationTrail: (params) => api.get('/beats/location-trail', { params }),
};

export const visitOptimizerApi = {
  tierInfo: () => api.get('/visit-optimizer/tier-info'),
  nearbyCheck: (data) => api.post('/visit-optimizer/nearby-check', data),
  optimizeDay: (data) => api.post('/visit-optimizer/optimize-day', data),
  getHistory: (params) => api.get('/visit-optimizer/history', { params }),
  adminOverview: () => api.get('/visit-optimizer/admin-overview'),
};

export default api;
